import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
// import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import DatePicker from "@insite/mobius/DatePicker";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ADIBannerSlide from "@NA/Components/ADIBannerSlide";
import ADIPdpBannerSlide from "@NA/Components/ProductDetails/ADIPdpBannerSlide";
import ADIMiniBannerSlide from "@NA/Components/ProductList/ADIMiniBannerSlide";
import ScrollToTop from "@NA/Components/ScrollToTop";
import { addBanner, BannerModel, getBanner, GetBannerParameter } from "@NA/Services/ADIBannerService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { ADIBannerSettings } from "@NA/Utils/ADIBannerSettings";
import ADIColors from "@NA/Utils/ADIColors";
import { formatDate, formatDateTime, formatEndDateTime } from "@NA/Utils/Utility";
import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

interface ADIBannerFormStyles {
    loadingOverlay?: LoadingOverlayProps;
    groupLeft?: InjectableCss;
    labelText?: TypographyProps;
    labelGridItem?: InjectableCss;
    listGridItem?: InjectableCss;
    list?: InjectableCss;
    checkboxGridItem?: InjectableCss;
    errorText?: TypographyProps;
    inputLabelOne?: TypographyProps;
    inputLabelTwo?: TypographyProps;
    divider?: InjectableCss;
    button?: ButtonPresentationProps;
    buttonText?: TypographyProps;
}

const styles: ADIBannerFormStyles = {
    loadingOverlay: {
        css: css`
            & > div:first-child[data-loading="true"] {
                position: fixed;
            }
        `,
    },
    groupLeft: {
        css: css`
            border-right: 1px solid ${ADIColors.gray2};
        `,
    },
    labelText: {
        css: css`
            font-size: 14px;
            font-weight: bold;
        `,
    },
    labelGridItem: {
        css: css`
            padding-bottom: 0px;
        `,
    },
    listGridItem: {
        css: css`
            border: 1px solid ${ADIColors.gray2};
            margin: 0px;
        `,
    },
    list: {
        css: css`
            max-height: 300px;
            overflow: auto;
            width: 100%;
            margin: 0px;
        `,
    },
    checkboxGridItem: {
        css: css`
            flex-direction: column;
        `,
    },
    errorText: {
        color: ADIColors.danger,
        css: css`
            font-size: 14px;
            font-weight: bold;
            color: ${ADIColors.danger};
            padding-top: 8px;
        `,
    },
    inputLabelOne: {
        css: css`
            flex-grow: 1;
        `,
    },
    inputLabelTwo: {
        css: css`
            color: ${ADIColors.gray4};
        `,
    },
    divider: {
        css: css`
            border-top: 1px solid ${ADIColors.gray2};
            margin-top: 12px;
            width: 100%;
        `,
    },
    button: {
        css: css`
            width: 100%;
        `,
    },
    buttonText: {
        color: ADIColors.blue5,
        css: css`
            font-size: 16px;
            font-weight: 700;
        `,
    },
};

const defaultBanner = {
    isMiniBanner: false,
    isPDPBanner: false,
    neverExpire: false,
    persona: "All",
    promoLayout: "onepromolayout",
    cssClass: "light",
    isNewTab: false,
    publish: true,
    isRewardEnrolledUser: false,
    isRewardInvitedUser: false,
};

const defaultBannerErrors = {
    description: false,
    pageUrl: false,
    startDate: false,
    endDate: false,
    rank: false,
    site: false,
    backgroundImage: false,
    link: false,
};

const ADIBannerForm: FC = () => {
    const history = useHistory();
    const currentState = useSelector((state: CustomApplicationState) => state);

    const [banner, setBanner] = useState<BannerModel>(defaultBanner as BannerModel);
    const [bannerErrors, setBannerErrors] = useState(defaultBannerErrors);

    const [pageUrl, setPageUrl] = useState("");
    const [pageUrlType, setPageUrlType] = useState("ADINumber");
    const [noEndPointUrl, setNoEndPointUrl] = useState(false);

    const toasterContext = useContext(ToasterContext);
    const [loading, setLoading] = useState(false);

    async function fetchBanner(id: string) {
        setLoading(true);
        const response = await getBanner({ parameter: id } as GetBannerParameter);
        if (response) {
            setBanner(response);
        } else {
            toasterContext.addToast({
                messageType: "danger",
                body: translate("Unable to find banner, please try again!"),
            });
        }
        setLoading(false);
    }

    const handleAddPageUrl = () => {
        let url = "";
        const pageUrls = banner.pageRelativeURL ? banner.pageRelativeURL.split(",") : [];

        if (banner.isPDPBanner) {
            url = `${pageUrlType}:${pageUrl}`;
        } else {
            url = pageUrl;
        }

        const index = pageUrls.indexOf(url);
        if (index === -1) {
            pageUrls.push(url);
            setBanner({ ...banner, pageRelativeURL: pageUrls.toString() });
        }

        setPageUrl("");
        setPageUrlType("ADINumber");
    };

    const handleRemovePageUrl = (index: number) => {
        const pageUrls = banner.pageRelativeURL.split(",");
        pageUrls.splice(index, 1);
        setBanner({ ...banner, pageRelativeURL: pageUrls.toString() });
    };

    const handleAuthChange = (type: string, add: boolean) => {
        const auths = banner.bannerAccessLevel ? banner.bannerAccessLevel.split(",") : [];

        if (add) {
            auths.push(type);
        } else {
            const index = auths.indexOf(type);
            auths.splice(index, 1);
        }

        setBanner({ ...banner, bannerAccessLevel: auths.toString() });
    };

    const handleSiteChange = (site: string, add: boolean) => {
        const sites = banner.siteName ? banner.siteName.split(",") : [];

        if (add) {
            sites.push(site);
        } else {
            const index = sites.indexOf(site);
            sites.splice(index, 1);
        }

        setBanner({ ...banner, siteName: sites.toString() });
    };

    function validateFields() {
        let validated = true;
        const errors = { ...defaultBannerErrors };
        if (!banner.description || banner.description === "") {
            errors.description = true;
            validated = false;
        }

        if (!banner.pageRelativeURL || banner.pageRelativeURL === "") {
            errors.pageUrl = true;
            validated = false;
        }

        if (!banner.startDate || banner.startDate === "") {
            errors.startDate = true;
            validated = false;
        }

        if ((!banner.endDate || banner.endDate === "") && !banner.neverExpire) {
            errors.endDate = true;
            validated = false;
        }

        if (!banner.siteName || banner.siteName === "") {
            errors.site = true;
            validated = false;
        }

        if (!banner.backgroundImage || banner.backgroundImage === "") {
            errors.backgroundImage = true;
            validated = false;
        }

        if ((!banner.bannerLinkUrl || banner.bannerLinkUrl === "") && !noEndPointUrl) {
            errors.link = true;
            validated = false;
        }

        if (validated) {
            setBannerErrors(defaultBannerErrors);
        } else {
            setBannerErrors(errors);
            toasterContext.addToast({
                messageType: "danger",
                body: translate("One or more fields failed validation, please correct and try again!"),
            });
        }

        return validated;
    }

    const handleSave = async () => {
        const validated = validateFields() as boolean;
        if (validated) {
            setLoading(true);
            const response = await addBanner(banner);
            if (response.successful) {
                const searchParams = new URLSearchParams(currentState.data.pages.location.search);
                searchParams.has("id")
                    ? fetchBanner(searchParams.get("id") || "")
                    : history.push("/MyAccount/BannerList");
                toasterContext.addToast({
                    messageType: "success",
                    body: translate(`Banner has been ${banner.id ? "updated" : "created"} !`),
                });
            } else {
                toasterContext.addToast({
                    messageType: "danger",
                    body: translate(`Unable to ${banner.id ? "update" : "create"} the banner, please try again!`),
                });
            }
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(currentState.data.pages.location.search);
        searchParams.has("id") && fetchBanner(searchParams.get("id") || "");

        if (typeof window !== undefined) {
            setTimeout(() => {
                window.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }, 100);
        }
    }, []);

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={loading}>
            <GridContainer>
                <GridItem width={12}>
                    {banner.isMiniBanner ? (
                        <ADIMiniBannerSlide slide={banner as any} />
                    ) : banner.isPDPBanner ? (
                        <ADIPdpBannerSlide slide={banner as any} />
                    ) : (
                        <ADIBannerSlide slide={banner as any} onClick={() => { }} />
                    )}
                </GridItem>
                <GridItem width={6} {...styles.groupLeft}>
                    <GridContainer gap={12}>
                        <GridItem width={12}>
                            <Typography variant="h3">{translate("Banner Info")}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Banner Name")}
                                value={banner.description || ""}
                                placeholder={translate("Banner Description Goes Here")}
                                required
                                error={bannerErrors.description && translate("Please enter a description!")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, description: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12} {...styles.labelGridItem}>
                            <Typography {...styles.labelText}>{translate("Loyalty User")}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox
                                checked={banner.isRewardEnrolledUser ?? false}
                                onChange={(e, currentValue) => setBanner({ ...banner, isRewardEnrolledUser: currentValue })}
                            > {translate("Enrolled Pro Rewards users")}
                            </Checkbox>
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox
                                checked={banner.isRewardInvitedUser}
                                onChange={(e, currentValue) => setBanner({ ...banner, isRewardInvitedUser: currentValue })}
                            > {translate("Invited Pro Rewards users")}
                            </Checkbox>
                        </GridItem>
                        <GridItem width={12}>
                            <Select
                                label={translate("Banner Type")}
                                value={banner.isPDPBanner ? "pdp" : banner.isMiniBanner ? "mini" : "wide"}
                                required
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                                    e.target.value === "mini" &&
                                        setBanner({ ...banner, isMiniBanner: true, isPDPBanner: false });
                                    e.target.value === "pdp" &&
                                        setBanner({ ...banner, isMiniBanner: false, isPDPBanner: true });
                                    e.target.value === "wide" &&
                                        setBanner({ ...banner, isMiniBanner: false, isPDPBanner: false });
                                }}
                            >
                                <option key={0} value="wide">
                                    {translate("Wide")}
                                </option>
                                <option key={1} value="mini">
                                    {translate("Mini")}
                                </option>
                                <option key={2} value="pdp">
                                    {translate("PDP")}
                                </option>
                            </Select>
                        </GridItem>
                        {banner.isPDPBanner && (
                            <GridItem width={12}>
                                <Select
                                    label={translate("URL Type")}
                                    value={pageUrlType}
                                    required
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setPageUrlType(e.target.value)}
                                >
                                    <option key={0} value="ADINumber">
                                        {translate("ADI Number")}
                                    </option>
                                    <option key={1} value="Brand">
                                        {translate("Brand Name")}
                                    </option>
                                    <option key={2} value="L3Category">
                                        {translate("L3 Category")}
                                    </option>
                                    <option key={3} value="L2Category">
                                        {translate("L2 Category")}
                                    </option>
                                    <option key={4} value="Default">
                                        {translate("Default")}
                                    </option>
                                </Select>
                            </GridItem>
                        )}
                        <GridItem width={12}>
                            <TextField
                                label={translate("Page Relative URL")}
                                value={pageUrl}
                                placeholder={translate(
                                    "EX:Home,5813-Central-Vac-Pipes. Add one item in the textbox and click the add button.",
                                )}
                                required
                                error={bannerErrors.pageUrl && translate("Please enter a page url or type url!")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setPageUrl(e.target.value)}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Button onClick={handleAddPageUrl} style={{ width: "25%" }}>
                                {translate("Add")}
                            </Button>
                        </GridItem>
                        <GridItem width={12} {...styles.labelGridItem}>
                            <Typography {...styles.labelText}>{translate("Current:")}</Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.listGridItem}>
                            <UnorderedList {...styles.list}>
                                {banner.pageRelativeURL ? (
                                    banner.pageRelativeURL?.split(",").map((row: string, index: number) => (
                                        <ListItem key={index}>
                                            {row}{" "}
                                            <Link onClick={() => handleRemovePageUrl(index)}>
                                                {translate("Remove")}
                                            </Link>
                                        </ListItem>
                                    ))
                                ) : (
                                    <ListItem>{translate("None")}</ListItem>
                                )}
                            </UnorderedList>
                        </GridItem>
                        <GridItem width={12}>
                            <DatePicker
                                label={translate("Start Date")}
                                format="MM/dd/y HH:mm"
                                required
                                error={bannerErrors.startDate && translate("Please enter a start date!")}

                                selectedDay={banner.startDate ? new Date(banner.startDate) : undefined}
                                onDayChange={date => {

                                    setBanner({
                                        ...banner,
                                        startDate: date.selectedDay ? formatDateTime(date.selectedDay) : "",
                                    })
                                }

                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <DatePicker
                                label={translate("End Date")}
                                disabled={banner.neverExpire}
                                format=" MM/dd/y HH:mm"
                                required
                                error={
                                    !banner.neverExpire && bannerErrors.endDate && translate("Please enter a end date!")
                                }
                                selectedDay={banner.endDate ? new Date(banner.endDate) : undefined}
                                onDayChange={date =>
                                    setBanner({
                                        ...banner,
                                        endDate: date.selectedDay ? formatEndDateTime(date.selectedDay) : "",
                                    })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox
                                checked={banner.neverExpire}
                                onChange={(e, currentValue) => setBanner({ ...banner, neverExpire: currentValue })}
                            >
                                {translate("Never Expire")}
                            </Checkbox>
                        </GridItem>
                        <GridItem width={12}>
                            <Select
                                label={translate("Authentication")}
                                value={banner.persona || ""}
                                required
                                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                    setBanner({ ...banner, persona: e.target.value })
                                }
                            >
                                <option key={0} value="All">
                                    {translate("All")}
                                </option>
                                <option key={1} value="Auth">
                                    {translate("Auth Only")}
                                </option>
                                <option key={2} value="UnAuth">
                                    {translate("UnAuth Only")}
                                </option>
                            </Select>
                        </GridItem>
                        {banner.persona === "Auth" && (
                            <GridItem width={12} {...styles.checkboxGridItem}>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("ORD") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("ORD", currentValue)}
                                >
                                    {translate("ORD (Full Access)")}
                                </Checkbox>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("SLS") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("SLS", currentValue)}
                                >
                                    {translate("SLS (ProductInfoOnly)")}
                                </Checkbox>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("PIO") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("PIO", currentValue)}
                                >
                                    {translate("PIO (ProductInventoryOnly)")}
                                </Checkbox>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("ALL") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("ALL", currentValue)}
                                >
                                    {translate("ALL (FullAccess + Accounting)")}
                                </Checkbox>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("INV") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("INV", currentValue)}
                                >
                                    {translate("INV (LimitedAccess)")}
                                </Checkbox>
                                <Checkbox
                                    checked={
                                        banner.bannerAccessLevel !== undefined &&
                                        banner.bannerAccessLevel.search("AR2") !== -1
                                    }
                                    onChange={(e, currentValue) => handleAuthChange("AR2", currentValue)}
                                >
                                    {translate("AR2 (LimitedAccess,Accounting)")}
                                </Checkbox>
                            </GridItem>
                        )}
                        <GridItem width={12}>
                            <TextField
                                label={translate("Rank")}
                                value={banner.rank ?? ""}
                                type="number"
                                required
                                error={bannerErrors.rank}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, rank: parseFloat(e.target.value) })
                                }
                            />
                        </GridItem>
                        <GridItem width={12} {...styles.labelGridItem}>
                            <Typography {...styles.labelText}>{translate("Website *")}</Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.checkboxGridItem}>
                            <Checkbox
                                error={bannerErrors.site}
                                checked={
                                    banner.siteName !== undefined &&
                                    banner.siteName.search(ADIBannerSettings.websiteCode.US) !== -1
                                }
                                onChange={(e, currentValue) =>
                                    handleSiteChange(ADIBannerSettings.websiteCode.US, currentValue)
                                }
                            >
                                {translate("US")}
                            </Checkbox>
                            <Checkbox
                                error={bannerErrors.site}
                                checked={
                                    banner.siteName !== undefined &&
                                    banner.siteName.search(ADIBannerSettings.websiteCode.CAEN) !== -1
                                }
                                onChange={(e, currentValue) =>
                                    handleSiteChange(ADIBannerSettings.websiteCode.CAEN, currentValue)
                                }
                            >
                                {translate("CAEN")}
                            </Checkbox>
                            <Checkbox
                                error={bannerErrors.site}
                                checked={
                                    banner.siteName !== undefined &&
                                    banner.siteName.search(ADIBannerSettings.websiteCode.CAFR) !== -1
                                }
                                onChange={(e, currentValue) =>
                                    handleSiteChange(ADIBannerSettings.websiteCode.CAFR, currentValue)
                                }
                            >
                                {translate("CAFR")}
                            </Checkbox>
                            <Checkbox
                                error={bannerErrors.site}
                                checked={
                                    banner.siteName !== undefined &&
                                    banner.siteName.search(ADIBannerSettings.websiteCode.PR) !== -1
                                }
                                onChange={(e, currentValue) =>
                                    handleSiteChange(ADIBannerSettings.websiteCode.PR, currentValue)
                                }
                            >
                                {translate("PR")}
                            </Checkbox>
                            {bannerErrors.site && (
                                <Typography {...styles.errorText}>{translate("Please select a site!")}</Typography>
                            )}
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Customer #")}
                                value={banner.customerNumber || ""}
                                placeholder={translate("EX: D4529,L6120")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, customerNumber: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("DC")}
                                value={banner.dc || ""}
                                placeholder={translate("EX: AB,NV")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, dc: e.target.value })
                                }
                            />
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem width={6}>
                    <GridContainer gap={12}>
                        <GridItem width={12}>
                            <Typography variant="h3">{translate("Banner Content")}</Typography>
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Background Image URL (2000px x 500px)")}
                                required={true}
                                error={bannerErrors.backgroundImage && translate("Please enter a background image!")}
                                value={banner.backgroundImage || ""}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, backgroundImage: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Product Image URL (530px x 390px)")}
                                value={banner.imageURL || ""}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, imageURL: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Mobile Background Image(375px x 320px)")}
                                value={banner.centerBoxbackground || ""}
                                disabled={banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, centerBoxbackground: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Image Alt Text")}
                                value={banner.imageAlternativeText || ""}
                                placeholder={translate("Please Enter Image Alternative Text")}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, imageAlternativeText: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Select
                                label={translate("Background Image is")}
                                value={banner.cssClass || ""}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                    setBanner({ ...banner, cssClass: e.target.value })
                                }
                            >
                                <option key={0} value="light">
                                    {translate("Light")}
                                </option>
                                <option key={1} value="dark">
                                    {translate("Dark")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Logo(s) Image Url (370px x 40px)")}
                                value={banner.logoImageUrl || ""}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, logoImageUrl: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Select
                                label={translate("Promo Layout")}
                                value={banner.promoLayout || ""}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                    setBanner({ ...banner, promoLayout: e.target.value })
                                }
                            >
                                <option key={0} value="onepromolayout">
                                    {translate("One Promo Layout")}
                                </option>
                                <option key={1} value="threePromolayout">
                                    {translate("Three Promo Layout")}
                                </option>
                            </Select>
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>
                                            {translate("Header Text Line 1")}
                                        </Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                ${banner.headerLineOne
                                                    ? `${ADIBannerSettings.headerOneLength -
                                                    banner.headerLineOne.length
                                                    }`
                                                    : `${ADIBannerSettings.headerOneLength}`
                                                }
                                                characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.headerLineOne || ""}
                                maxLength={ADIBannerSettings.headerOneLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, headerLineOne: e.target.value })
                                }
                            />
                        </GridItem>
                        {banner.promoLayout === "onepromolayout" && (
                            <GridItem width={12}>
                                <TextField
                                    label={
                                        <>
                                            <Typography {...styles.inputLabelOne}>
                                                {translate("Header Text Line 2")}
                                            </Typography>
                                            <Typography {...styles.inputLabelTwo}>
                                                {translate(`
                                                    ${banner.headerLineTwo
                                                        ? `${ADIBannerSettings.headerTwoLength -
                                                        banner.headerLineTwo.length
                                                        }`
                                                        : `${ADIBannerSettings.headerTwoLength}`
                                                    }
                                                    characters left`)}
                                            </Typography>
                                        </>
                                    }
                                    labelProps={{
                                        css: css`
                                            display: flex;
                                        `,
                                    }}
                                    value={banner.headerLineTwo || ""}
                                    maxLength={ADIBannerSettings.headerTwoLength}
                                    disabled={banner.isMiniBanner || banner.isPDPBanner}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setBanner({ ...banner, headerLineTwo: e.target.value })
                                    }
                                />
                            </GridItem>
                        )}
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>{translate("Copy 1")}</Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                    ${banner.copyTextOne
                                                    ? `${ADIBannerSettings.copyOneLength -
                                                    banner.copyTextOne.length
                                                    }`
                                                    : `${ADIBannerSettings.copyOneLength}`
                                                }
                                                    characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.copyTextOne || ""}
                                maxLength={ADIBannerSettings.copyOneLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, copyTextOne: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>{translate("Copy 2")}</Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                    ${banner.copyTextTwo
                                                    ? `${ADIBannerSettings.copyTwoLength -
                                                    banner.copyTextTwo.length
                                                    }`
                                                    : `${ADIBannerSettings.copyTwoLength}`
                                                }
                                                    characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.copyTextTwo || ""}
                                maxLength={ADIBannerSettings.copyTwoLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, copyTextTwo: e.target.value })
                                }
                            />
                        </GridItem>
                        {banner.promoLayout === "threePromolayout" && (
                            <GridItem width={12}>
                                <TextField
                                    label={
                                        <>
                                            <Typography {...styles.inputLabelOne}>{translate("Copy 3")}</Typography>
                                            <Typography {...styles.inputLabelTwo}>
                                                {translate(`
                                                    ${banner.copyTextThree
                                                        ? `${ADIBannerSettings.copyThreeLength -
                                                        banner.copyTextTwo.length
                                                        }`
                                                        : `${ADIBannerSettings.copyThreeLength}`
                                                    }
                                                    characters left`)}
                                            </Typography>
                                        </>
                                    }
                                    labelProps={{
                                        css: css`
                                            display: flex;
                                        `,
                                    }}
                                    value={banner.copyTextThree || ""}
                                    maxLength={ADIBannerSettings.copyThreeLength}
                                    disabled={banner.isMiniBanner || banner.isPDPBanner}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setBanner({ ...banner, copyTextThree: e.target.value })
                                    }
                                />
                            </GridItem>
                        )}
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>{translate("Small Copy 1")}</Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                    ${banner.smallCopyTextOne
                                                    ? `${ADIBannerSettings.smallCopyOneLength -
                                                    banner.smallCopyTextOne.length
                                                    }`
                                                    : `${ADIBannerSettings.smallCopyOneLength}`
                                                }
                                                    characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.smallCopyTextOne || ""}
                                maxLength={ADIBannerSettings.smallCopyOneLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, smallCopyTextOne: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>{translate("Small Copy 2")}</Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                    ${banner.smallCopyTextTwo
                                                    ? `${ADIBannerSettings.smallCopyTwoLength -
                                                    banner.smallCopyTextTwo.length
                                                    }`
                                                    : `${ADIBannerSettings.smallCopyTwoLength}`
                                                }
                                                    characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.smallCopyTextTwo || ""}
                                maxLength={ADIBannerSettings.smallCopyTwoLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, smallCopyTextTwo: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("CTA for Small Copy 2")}
                                value={banner.callToAction || ""}
                                placeholder={translate("EX: /Catalog/shop-brands/Hikvision")}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, callToAction: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={
                                    <>
                                        <Typography {...styles.inputLabelOne}>{translate("Button Text")}</Typography>
                                        <Typography {...styles.inputLabelTwo}>
                                            {translate(`
                                                    ${banner.bannerButtonText
                                                    ? `${ADIBannerSettings.buttonTextLength -
                                                    banner.bannerButtonText.length
                                                    }`
                                                    : `${ADIBannerSettings.buttonTextLength}`
                                                }
                                                    characters left`)}
                                        </Typography>
                                    </>
                                }
                                labelProps={{
                                    css: css`
                                        display: flex;
                                    `,
                                }}
                                value={banner.bannerButtonText || ""}
                                maxLength={ADIBannerSettings.buttonTextLength}
                                disabled={banner.isMiniBanner || banner.isPDPBanner}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, bannerButtonText: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox
                                checked={noEndPointUrl}
                                onChange={(e, currentValue) => {
                                    setNoEndPointUrl(currentValue);
                                    currentValue && setBanner({ ...banner, bannerLinkUrl: "", isNewTab: false });
                                }}
                            >
                                {translate("No endpoint URL")}
                            </Checkbox>
                        </GridItem>
                        <GridItem width={12}>
                            <TextField
                                label={translate("Button & Banner Link")}
                                value={banner.bannerLinkUrl || ""}
                                required
                                error={bannerErrors.link && translate("Please enter a link!")}
                                placeholder={translate("Link URL for Banner and Button")}
                                disabled={noEndPointUrl}
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    setBanner({ ...banner, bannerLinkUrl: e.target.value })
                                }
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Checkbox
                                checked={banner.isNewTab}
                                disabled={noEndPointUrl}
                                onChange={(e, currentValue) => setBanner({ ...banner, isNewTab: currentValue })}
                            >
                                {translate("Open in a New Tab")}
                            </Checkbox>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem width={12}>
                    <StyledWrapper {...styles.divider} />
                </GridItem>
                <GridItem width={2}>
                    <Button
                        buttonType="outline"
                        {...styles.button}
                        onClick={() => history.push("/MyAccount/BannerList")}
                    >
                        <Typography {...styles.buttonText}>{translate("Exit To List")}</Typography>
                    </Button>
                </GridItem>
                <GridItem width={7} />
                <GridItem width={3}>
                    <Button {...styles.button} type="submit" onClick={handleSave}>
                        {translate(banner.id ? "Update" : "Add New")}
                    </Button>
                </GridItem>
            </GridContainer>
            <ScrollToTop />
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: ADIBannerForm,
    definition: {
        group: "My Account",
        displayName: "ADI Banner Form",
    } as WidgetDefinition,
};

export default widgetModule;
